import { inject, Injectable } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { QUERY_KEYS } from '@core/constans';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { ApiResponse } from '@core/types';
import { Resident } from '@core/types/unit.type';
import {
  CouncilMember,
  CouncilMemberCreate,
  CouncilMemberFromResidentCreate,
  CouncilMemberUpdate,
} from '@core/types/council.type';
import { CouncilService as CouncilApiService } from '@core/services/council.service';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class CouncilQuery {
  private readonly service = inject(CouncilApiService);
  private readonly queryClient = inject(QueryClient);

  useCouncilGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const c = config();
      return {
        queryKey: QUERY_KEYS.council.grid(c),
        queryFn: () => lastValueFrom(this.service.grid(c)),
        select: (res: ApiResponse<PaginationResult<CouncilMember>>) => res.data,
        staleTime: environment.staleTime,
      };
    });
  }

  useCreateCouncilMember() {
    return injectMutation(() => ({
      mutationFn: (data: CouncilMemberCreate) => lastValueFrom(this.service.create(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.council.all }),
    }));
  }

  useUpdateCouncilMember() {
    return injectMutation(() => ({
      mutationFn: (data: CouncilMemberUpdate) => lastValueFrom(this.service.update(data)),
      onSuccess: (_res, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.council.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.council.all });
      },
    }));
  }

  useDeleteCouncilMember() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.service.delete(id)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.council.all }),
    }));
  }

  useActiveResidents() {
    return injectQuery(() => ({
      queryKey: QUERY_KEYS.council.activeResidents(),
      queryFn: () => lastValueFrom(this.service.getActiveResidents()),
      select: (res: ApiResponse<Resident[]>) => res.data ?? [],
      staleTime: environment.staleTime,
    }));
  }

  useCreateFromResident() {
    return injectMutation(() => ({
      mutationFn: (data: CouncilMemberFromResidentCreate) => lastValueFrom(this.service.createFromResident(data)),
      onSuccess: () => this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.council.all }),
    }));
  }
}

